class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.sectionHeader = this.closest('.section-header');
    this.desktopMediaQuery = window.matchMedia('(min-width: 990px)');
    this.triggerLink = this.querySelector('.mega-menu__trigger-link');

    this.onMouseEnter = this.handleMouseEnter.bind(this);
    this.onMouseLeave = this.handleMouseLeave.bind(this);
    this.onTriggerLinkMouseDown = this.handleTriggerLinkMouseDown.bind(this);
    this.onTriggerLinkClick = this.handleTriggerLinkClick.bind(this);

    this.addEventListener('mouseenter', this.onMouseEnter);
    this.addEventListener('mouseleave', this.onMouseLeave);
    if (this.triggerLink) {
      this.triggerLink.addEventListener('mousedown', this.onTriggerLinkMouseDown);
      this.triggerLink.addEventListener('click', this.onTriggerLinkClick);
    }
    this.bindOverlayClose();
    this.bindViewportHandlers();
  }

  handleTriggerLinkMouseDown(event) {
    event.stopPropagation();
  }

  handleTriggerLinkClick(event) {
    event.stopPropagation();
    if (this.desktopMediaQuery.matches) this.close();
  }

  bindOverlayClose() {
    if (HeaderMenu.overlayCloseBound) return;

    document.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element) || !target.closest('.mega-menu-overlay')) return;

      document.querySelectorAll('header-menu').forEach((disclosure) => disclosure.close());
    });

    HeaderMenu.overlayCloseBound = true;
  }

  handleMouseEnter() {
    if (!this.desktopMediaQuery.matches) return;

    window.clearTimeout(this.closeTimer);
    this.openByHover();
  }

  handleMouseLeave() {
    if (!this.desktopMediaQuery.matches) return;

    window.clearTimeout(this.closeTimer);
    this.closeTimer = window.setTimeout(() => this.close(), 100);
  }

  openByHover() {
    document.querySelectorAll('header-menu').forEach((disclosure) => {
      if (disclosure !== this) disclosure.close();
    });

    if (!this.mainDetailsToggle.hasAttribute('open')) {
      this.mainDetailsToggle.setAttribute('open', '');
      this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', true);
    }

    this.updateHeaderBottomPosition();
    this.updateMegaMenuPosition();
    this.syncOpenState();
  }

  bindViewportHandlers() {
    if (HeaderMenu.viewportHandlersBound) return;

    const recalcOpenMenus = () => {
      document.querySelectorAll('header-menu').forEach((disclosure) => disclosure.repositionIfOpen());
    };

    window.addEventListener('resize', recalcOpenMenus);
    window.addEventListener('scroll', recalcOpenMenus, { passive: true });
    HeaderMenu.viewportHandlersBound = true;
  }

  updateHeaderBottomPosition() {
    if (!this.header) return;

    const headerBottom = this.header.getBoundingClientRect().bottom;
    const headerHeight = this.header.offsetHeight;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${headerBottom}px`
    );
    document.documentElement.style.setProperty('--mega-menu-header-height', `${headerHeight}px`);
  }

  updateMegaMenuPosition() {
    if (!this.desktopMediaQuery.matches) {
      this.mainDetailsToggle.style.removeProperty('--mega-menu-offset-x');
      this.mainDetailsToggle.style.removeProperty('--mega-menu-offset-y');
      return;
    }

    const content = this.mainDetailsToggle.querySelector('.mega-menu__content');
    const summary = this.mainDetailsToggle.querySelector('summary');
    if (!content || !summary) return;

    const detailsRect = this.mainDetailsToggle.getBoundingClientRect();
    const summaryRect = summary.getBoundingClientRect();
    const triggerLabel = summary.querySelector('.mega-menu__trigger-label');
    const triggerRect = triggerLabel ? triggerLabel.getBoundingClientRect() : summaryRect;
    const headerRect = this.header ? this.header.getBoundingClientRect() : null;
    const detailsStyles = getComputedStyle(this.mainDetailsToggle);

    let maxMenuWidth = parseFloat(detailsStyles.getPropertyValue('--mega-menu-max-width'));
    if (Number.isNaN(maxMenuWidth) || maxMenuWidth <= 0) maxMenuWidth = 850;

    const viewportGutter = 16;
    const viewportWidth = window.innerWidth;
    const menuWidth = Math.min(maxMenuWidth, Math.max(0, window.innerWidth - viewportGutter * 2));
    const minLeft = viewportGutter;
    const maxLeft = viewportWidth - viewportGutter - menuWidth;
    const centeredLeft = triggerRect.left + triggerRect.width / 2 - menuWidth / 2;
    const resolvedLeft = Math.min(Math.max(centeredLeft, minLeft), maxLeft);

    const localOffset = resolvedLeft - detailsRect.left;
    const offsetYAdjustmentPx = 0;
    const localTop = headerRect
      ? headerRect.bottom - detailsRect.top + offsetYAdjustmentPx
      : detailsRect.height + offsetYAdjustmentPx;

    this.mainDetailsToggle.style.setProperty('--mega-menu-offset-x', `${Math.round(localOffset)}px`);
    this.mainDetailsToggle.style.setProperty('--mega-menu-offset-y', `${localTop}px`);
  }

  repositionIfOpen() {
    if (!this.mainDetailsToggle.hasAttribute('open')) return;
    this.updateHeaderBottomPosition();
    this.updateMegaMenuPosition();
  }

  onToggle() {
    if (!this.header) return;

    this.header.preventHide = this.mainDetailsToggle.open;
    this.updateHeaderBottomPosition();
    this.updateMegaMenuPosition();
    this.syncOpenState();
  }

  close() {
    window.clearTimeout(this.closeTimer);
    super.close();
    this.mainDetailsToggle.style.removeProperty('--mega-menu-offset-x');
    this.mainDetailsToggle.style.removeProperty('--mega-menu-offset-y');
    this.syncOpenState();
  }

  syncOpenState() {
    if (!this.sectionHeader) return;

    const hasOpenMegaMenu = this.sectionHeader.querySelector('.mega-menu[open]') !== null;
    this.sectionHeader.classList.toggle('mega-menu-open', hasOpenMegaMenu);
  }
}

customElements.define('header-menu', HeaderMenu);
